import { useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AdminTable, type ColDef } from "@/components/admin/shared/AdminTable";
import { AdminModal } from "@/components/admin/shared/AdminModal";
import { DeleteConfirmModal } from "@/components/admin/shared/DeleteConfirmModal";
import {
  PageHeader,
  AddButton,
  SaveButton,
  CancelButton,
  ViewButton,
  EditButton,
  DeleteButton,
} from "@/components/admin/shared/PageHeader";
import { TableSearch } from "@/components/admin/shared/TableSearch";
import { PennField, PennMultiSelect, PennDropdown } from "@/components/ui";
import {
  responsibilityMapSchema,
  type ResponsibilityMapFormData,
} from "@/types/admin/responsibilityMap.types";
import {
  useCreateResponsibilityMap,
  useDeleteResponsibilityMap,
  useResponsibilityRecipientUsers,
  useResponsibilityMap,
  useResponsibilityMapOptions,
  useUpdateResponsibilityMap,
} from "@/hooks/useResponsibilityMap";
import type { ResponsibilityMap } from "@/api/responsibilityMap";
import type { AdminUser } from "@/api/users";

type ModalMode = "add" | "edit" | "view";
type ModalState = { mode: ModalMode; item?: ResponsibilityMap } | null;

const normalizeEmail = (value: string) => value.trim().toLowerCase();

const isEmail = (value: string) =>
  z.string().email().safeParse(normalizeEmail(value)).success;

const normalizeEmailList = (values: string[]) =>
  Array.from(new Set(values.map(normalizeEmail).filter(Boolean)));

const recipientOptions = (users: AdminUser[]) => {
  const byEmail = new Map<string, { value: string; label: string }>();
  for (const user of users) {
    const email = normalizeEmail(user.email);
    const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
    const role = user.role?.displayName ?? user.role?.code ?? "No active role";
    const region = user.region?.code ?? "No region";
    byEmail.set(email, {
      value: email,
      label: `${name || email} — ${email} · ${role} · ${region}`,
    });
  }
  return [...byEmail.values()].sort((a, b) => a.label.localeCompare(b.label));
};

const MAX_RECIPIENT_EMAIL_LENGTH = 255;
const invalidRecipientCreateMessage = (value: string): string | null => {
  const email = normalizeEmail(value);
  if (!email) return null;
  if (email.length > MAX_RECIPIENT_EMAIL_LENGTH) {
    return `Email must be ${MAX_RECIPIENT_EMAIL_LENGTH} characters or fewer`;
  }
  if (!isEmail(email)) return "Enter a valid email address";
  return null;
};

const blank: ResponsibilityMapFormData = {
  responsibility: "",
  region: "",
  salesEmails: [],
  engineeringEmails: [],
};

const apiErrorMessage = (err: unknown, fallback: string): string => {
  if (err && typeof err === "object" && "response" in err) {
    const data = (
      err as { response?: { data?: { error?: { message?: string } } } }
    ).response?.data;
    if (data?.error?.message) return data.error.message;
  }
  return err instanceof Error ? err.message : fallback;
};

export const ResponsibilityMapPage = () => {
  const { data, isLoading, isError, error } = useResponsibilityMap();
  const allItems: ResponsibilityMap[] = useMemo(() => data?.data ?? [], [data]);

  // The endpoint returns every mapping (no pagination), so the recipient search
  // filters client-side across both the sales and engineering email lists.
  const [search, setSearch] = useState("");
  const items = useMemo(() => {
    const needle = normalizeEmail(search);
    if (!needle) return allItems;
    return allItems.filter((i) =>
      [...i.salesEmails, ...i.engineeringEmails].some((email) =>
        normalizeEmail(email).includes(needle),
      ),
    );
  }, [allItems, search]);

  const [modal, setModal] = useState<ModalState>(null);
  const [deleteTarget, setDeleteTarget] = useState<ResponsibilityMap | null>(
    null,
  );
  const [formError, setFormError] = useState<string | null>(null);

  // Dropdown options — fetched only while the modal is open.
  const optionsQuery = useResponsibilityMapOptions(
    modal !== null && modal.mode !== "view",
  );
  const responsibilityOptions = useMemo(
    () =>
      (optionsQuery.data?.responsibilities ?? []).map((r) => ({
        value: r,
        label: r,
      })),
    [optionsQuery.data],
  );
  const regionOptions = useMemo(
    () =>
      (optionsQuery.data?.regions ?? []).map((r) => ({ value: r, label: r })),
    [optionsQuery.data],
  );

  const recipientUsersQuery = useResponsibilityRecipientUsers(modal !== null);

  const createMutation = useCreateResponsibilityMap();
  const updateMutation = useUpdateResponsibilityMap();
  const deleteMutation = useDeleteResponsibilityMap();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<ResponsibilityMapFormData>({
    resolver: zodResolver(responsibilityMapSchema),
    defaultValues: blank,
  });

  // Both slots offer the same active internal directory without role or
  // mapping-region filtering. Labels retain role and region for context;
  // persistence remains email-only under MD-06.
  const registeredRecipientOptions = useMemo(
    () => recipientOptions(recipientUsersQuery.data?.internal ?? []),
    [recipientUsersQuery.data],
  );

  const openAdd = () => {
    setFormError(null);
    reset(blank);
    setModal({ mode: "add" });
  };
  const openEdit = (item: ResponsibilityMap) => {
    setFormError(null);
    reset(toForm(item));
    setModal({ mode: "edit", item });
  };
  const openView = (item: ResponsibilityMap) => {
    setFormError(null);
    reset(toForm(item));
    setModal({ mode: "view", item });
  };
  const closeModal = () => {
    setModal(null);
    setFormError(null);
  };

  const onSubmit = (form: ResponsibilityMapFormData) => {
    setFormError(null);
    const payload: ResponsibilityMapFormData = {
      ...form,
      // Enforce the canonical email contract even when an untouched legacy
      // mapping was loaded with mixed-case or surrounding whitespace.
      salesEmails: normalizeEmailList(form.salesEmails),
      engineeringEmails: normalizeEmailList(form.engineeringEmails),
    };
    if (modal?.mode === "edit" && modal.item) {
      updateMutation.mutate(
        { id: modal.item.id, body: payload },
        {
          onSuccess: closeModal,
          onError: (e) =>
            setFormError(apiErrorMessage(e, "Failed to save mapping.")),
        },
      );
    } else {
      createMutation.mutate(payload, {
        onSuccess: closeModal,
        onError: (e) =>
          setFormError(apiErrorMessage(e, "Failed to create mapping.")),
      });
    }
  };

  const isView = modal?.mode === "view";
  const saving = createMutation.isPending || updateMutation.isPending;

  const cols: ColDef<ResponsibilityMap>[] = [
    { header: "Responsibility", key: "responsibility" },
    { header: "Region", key: "region" },
    { header: "Sales emails", render: (i) => i.salesEmails.join(", ") },
    {
      header: "Engineering emails",
      render: (i) => i.engineeringEmails.join(", "),
    },
    {
      header: "Actions",
      render: (i) => (
        <div className="flex space-x-2">
          <ViewButton onClick={() => openView(i)} />
          <EditButton onClick={() => openEdit(i)} />
          <DeleteButton onClick={() => setDeleteTarget(i)} />
        </div>
      ),
    },
  ];

  const titleMap = {
    add: "Add mapping",
    edit: "Edit responsibility mapping",
    view: "View responsibility mapping",
  };

  return (
    <>
      <PageHeader
        title="Responsibility Map"
        subtitle="Record-level editor for mappings (id: id). These mappings drive the default 'My tasks' filter in the Selector."
        action={<AddButton onClick={openAdd} label="Add mapping" />}
      />

      {isError && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-red-700 text-body">
          Failed to load responsibility map
          {error instanceof Error ? `: ${error.message}` : "."}
        </div>
      )}

      <TableSearch
        value={search}
        onChange={setSearch}
        onClear={() => setSearch("")}
        placeholder="Search sales or engineering email…"
      />

      <AdminTable
        columns={cols}
        rows={items}
        keyExtractor={(i) => i.id}
        loading={isLoading}
      />

      <AdminModal
        open={modal !== null}
        onClose={closeModal}
        title={modal ? titleMap[modal.mode] : ""}
        maxWidth="max-w-lg"
        footer={
          isView ? (
            <CancelButton onClick={closeModal} />
          ) : (
            <>
              <CancelButton onClick={closeModal} />
              <SaveButton
                onClick={handleSubmit(onSubmit)}
                label={saving ? "Saving…" : "Save"}
              />
            </>
          )
        }
      >
        <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
          {formError && (
            <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-red-700 text-body">
              {formError}
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PennField
              label="Responsibility"
              htmlFor="responsibility"
              required={!isView}
              error={!isView ? errors.responsibility?.message : undefined}
            >
              <Controller
                name="responsibility"
                control={control}
                render={({ field }) => (
                  <PennDropdown
                    inputId="responsibility"
                    options={responsibilityOptions}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isSearchable
                    isDisabled={isView}
                    error={!isView && !!errors.responsibility}
                    placeholder={
                      optionsQuery.isLoading
                        ? "Loading…"
                        : "Select responsibility…"
                    }
                  />
                )}
              />
            </PennField>
            <PennField
              label="Region"
              htmlFor="region"
              required={!isView}
              error={!isView ? errors.region?.message : undefined}
            >
              <Controller
                name="region"
                control={control}
                render={({ field }) => (
                  <PennDropdown
                    inputId="region"
                    options={regionOptions}
                    value={field.value ?? ""}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    isSearchable
                    isDisabled={isView}
                    error={!isView && !!errors.region}
                    placeholder={
                      optionsQuery.isLoading ? "Loading…" : "Select region…"
                    }
                  />
                )}
              />
            </PennField>
          </div>
          {recipientUsersQuery.isError && !isView && (
            <div
              className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800 text-body"
              role="alert"
            >
              Registered-user suggestions could not be loaded. You can still add
              a valid custom email.
            </div>
          )}
          <PennField
            label="Sales responsibility"
            htmlFor="salesEmails"
            required={!isView}
            error={!isView ? errors.salesEmails?.message : undefined}
            hint={
              isView
                ? undefined
                : "Select any active internal user"
            }
          >
            <Controller
              name="salesEmails"
              control={control}
              render={({ field }) => (
                <PennMultiSelect
                  inputId="salesEmails"
                  options={registeredRecipientOptions}
                  value={field.value ?? []}
                  onChange={(values) =>
                    setValue("salesEmails", normalizeEmailList(values), {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  onBlur={field.onBlur}
                  isDisabled={isView}
                  isLoading={recipientUsersQuery.isLoading}
                  error={!isView && !!errors.salesEmails}
                  allowCreate
                  isValidNewOption={(value) => {
                    const email = normalizeEmail(value);
                    return (
                      email.length <= MAX_RECIPIENT_EMAIL_LENGTH &&
                      isEmail(email) &&
                      !(field.value ?? []).some(
                        (selected) => normalizeEmail(selected) === email,
                      ) &&
                      !registeredRecipientOptions.some(
                        (option) => option.value === email,
                      )
                    );
                  }}
                  formatCreateLabel={(value) =>
                    `Add custom email “${normalizeEmail(value)}”`
                  }
                  invalidCreateMessage={invalidRecipientCreateMessage}
                  placeholder="Search active internal users…"
                />
              )}
            />
          </PennField>
          <PennField
            label="Engineering responsibility"
            htmlFor="engineeringEmails"
            required={!isView}
            error={!isView ? errors.engineeringEmails?.message : undefined}
            hint={
              isView
                ? undefined
                : "Select any active internal user"
            }
          >
            <Controller
              name="engineeringEmails"
              control={control}
              render={({ field }) => (
                <PennMultiSelect
                  inputId="engineeringEmails"
                  options={registeredRecipientOptions}
                  value={field.value ?? []}
                  onChange={(values) =>
                    setValue("engineeringEmails", normalizeEmailList(values), {
                      shouldDirty: true,
                      shouldValidate: true,
                    })
                  }
                  onBlur={field.onBlur}
                  isDisabled={isView}
                  isLoading={recipientUsersQuery.isLoading}
                  error={!isView && !!errors.engineeringEmails}
                  allowCreate
                  isValidNewOption={(value) => {
                    const email = normalizeEmail(value);
                    return (
                      email.length <= MAX_RECIPIENT_EMAIL_LENGTH &&
                      isEmail(email) &&
                      !(field.value ?? []).some(
                        (selected) => normalizeEmail(selected) === email,
                      ) &&
                      !registeredRecipientOptions.some(
                        (option) => option.value === email,
                      )
                    );
                  }}
                  formatCreateLabel={(value) =>
                    `Add custom email “${normalizeEmail(value)}”`
                  }
                  invalidCreateMessage={invalidRecipientCreateMessage}
                  placeholder="Search active internal users…"
                />
              )}
            />
          </PennField>
        </form>
      </AdminModal>

      <DeleteConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteMutation.mutate(deleteTarget.id, {
            onSettled: () => setDeleteTarget(null),
          });
        }}
        itemLabel={`${deleteTarget?.responsibility} – ${deleteTarget?.region}`}
      />
    </>
  );
};

/** Map an API record into the form shape. */
const toForm = (item: ResponsibilityMap): ResponsibilityMapFormData => ({
  responsibility: item.responsibility,
  region: item.region,
  salesEmails: item.salesEmails,
  engineeringEmails: item.engineeringEmails,
});
